import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Headphones, ChevronRight } from 'lucide-react';
import { useTheme } from '../context/ThemeContext';
import { useTranslation } from '../context/LanguageContext';
import { httpClient } from '../lib/http';
import { API_URL } from '../config/api';
import usePushBus from '../hooks/usePushBus';

/**
 * Thin home-page banner that surfaces unread admin replies (chat
 * messages + bug-thread responses). Mirrors the polling logic of
 * `<SupportButton />` (in Profilo) but with a banner-style UI suited
 * for the dashboard. Tapping the banner deep-links to /app/assistenza.
 *
 * The banner self-hides while no unread items exist, so the home
 * screen stays clean for users with nothing pending.
 */
export default function SupportReplyBanner() {
  const { isDark } = useTheme();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [unread, setUnread] = useState(0);

  const fetchUnread = useCallback(async () => {
    try {
      const { data } = await httpClient.get(`${API_URL}/api/support/unread`);
      setUnread((data?.chat_unread || 0) + (data?.bugs_with_replies || 0));
    } catch (_) { /* silent: banner just stays hidden */ }
  }, []);

  // Initial load + slow safety-net poll (30 s) covers the case where
  // the user opened a stale tab, missed the push, or the FCM client
  // is on a flaky connection. Live updates come via the push bus.
  useEffect(() => {
    fetchUnread();
    const interval = setInterval(fetchUnread, 30000);
    return () => clearInterval(interval);
  }, [fetchUnread]);

  // Push-driven instant refresh — whenever any push arrives we
  // re-fetch. The endpoint is cheap so we don't bother filtering by
  // type; we always want the freshest count.
  usePushBus(fetchUnread);

  if (unread <= 0) return null;

  // Pluralize the body copy. Avoids "1 nuovi messaggi" weirdness.
  const body = unread === 1
    ? t('support.newReplyBody')
    : t('support.newReplyBodyMulti', { count: unread });

  return (
    <div className="px-4 mb-3" data-testid="support-reply-banner">
      <button
        type="button"
        onClick={() => navigate('/app/assistenza')}
        className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl active:scale-[0.98] transition-transform shadow-sm"
        style={{
          background: isDark
            ? 'linear-gradient(135deg, rgba(232,51,74,0.18) 0%, rgba(34,211,238,0.15) 100%)'
            : 'linear-gradient(135deg, rgba(232,51,74,0.10) 0%, rgba(34,211,238,0.10) 100%)',
          border: `1px solid ${isDark ? 'rgba(232,51,74,0.35)' : 'rgba(232,51,74,0.25)'}`,
        }}
      >
        {/* Soft-pulsing icon chip — draws the eye without screaming */}
        <span
          className="relative w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0"
          style={{ background: 'linear-gradient(135deg, #E8334A 0%, #FF6B7B 100%)' }}
        >
          <Headphones className="w-4 h-4 text-white" />
          <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 rounded-full bg-white text-[#E8334A] text-[10px] font-bold flex items-center justify-center px-1 ring-2 ring-[#E8334A]/40">
            {unread > 9 ? '9+' : unread}
          </span>
        </span>

        <span className="flex-1 text-left min-w-0">
          <span className={`block text-sm font-semibold leading-tight ${isDark ? 'text-white' : 'text-gray-900'}`}>
            {t('support.newReplyTitle')}
          </span>
          <span className={`block text-[11px] truncate leading-tight mt-0.5 ${isDark ? 'text-white/60' : 'text-gray-600'}`}>
            {body}
          </span>
        </span>

        <ChevronRight className={`w-4 h-4 flex-shrink-0 ${isDark ? 'text-white/50' : 'text-gray-400'}`} />
      </button>
    </div>
  );
}
